import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImage } from "@/lib/api";

// Premium black/gold fullscreen lightbox with keyboard, swipe, and arrow nav.
// `images` is the raw array of image refs (storage keys or URLs); we resolve
// them via resolveImage when rendering. `startIndex` selects the initial slide.
export default function ImageLightbox({ images, startIndex = 0, onClose, alt = "" }) {
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef(null);

  const last = images.length - 1;
  const prev = useCallback(
    () => setIndex((i) => (i <= 0 ? last : i - 1)),
    [last]
  );
  const next = useCallback(
    () => setIndex((i) => (i >= last ? 0 : i + 1)),
    [last]
  );

  // Keyboard nav + lock body scroll while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next]);

  // Touch swipe
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx > 0) prev();
      else next();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0B0B0B]/97 backdrop-blur-sm flex items-center justify-center"
      data-testid="lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 md:top-7 md:right-7 z-10 w-11 h-11 flex items-center justify-center border border-white/15 text-white hover:border-[#C6A15B] hover:text-[#C6A15B] transition-colors"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <div
        className="absolute top-7 left-5 md:left-10 text-[10px] tracking-[0.36em] uppercase text-zinc-400"
        data-testid="lightbox-counter"
      >
        {String(index + 1).padStart(2, "0")}{" "}
        <span className="text-zinc-600">/</span>{" "}
        <span className="text-zinc-600">{String(images.length).padStart(2, "0")}</span>
      </div>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={prev}
          aria-label="Previous image"
          data-testid="lightbox-prev"
          className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border border-white/15 text-white hover:border-[#C6A15B] hover:text-[#C6A15B] transition-colors bg-black/30"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Image stage */}
      <div className="relative w-full h-full flex items-center justify-center px-4 md:px-24 py-20 md:py-24">
        <img
          src={resolveImage(images[index])}
          alt={alt}
          draggable={false}
          className="max-w-full max-h-full object-contain select-none"
        />
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={next}
          aria-label="Next image"
          data-testid="lightbox-next"
          className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border border-white/15 text-white hover:border-[#C6A15B] hover:text-[#C6A15B] transition-colors bg-black/30"
        >
          <ChevronRight size={22} />
        </button>
      )}
    </div>
  );
}
